import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import styles from './Join.module.css'

import EntryForm, { EntryFormContainer, EntryFormError } from 'Layout/EntryForm'
import Link from 'Design/components/Link'
import Heading from 'Design/components/Heading'
import Spinner from 'Design/components/Spinner'
import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import Button from 'Design/components/Button'
import FormErrorText from 'Design/components/FormErrorText'

import { useVerifyTokenQuery, useAcceptInviteMutation } from './join-account.service'
import joinFormValidationSchema from './join-form-validation-schema'

const JoinForm = ({ user, token }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      name: user.name,
      password: '',
      rePassword: '',
      apiError: '' // not a form field
    },
    resolver: yupResolver(joinFormValidationSchema)
  })

  const navigate = useNavigate()
  const [acceptInvite, acceptInviteResult] = useAcceptInviteMutation()

  const processError = (err = {}) => {
    switch (err.type) {
      default:
        setError('apiError', err)
    }
  }

  const onFormSubmit = async (data) => {
    clearErrors('apiError')

    const payload = {
      name: data.name,
      password: data.password,
      invitationToken: token
    }

    try {
      await acceptInvite(payload).unwrap()
      navigate('/login', { state: 'accountVerificationSuccess' })
    } catch (e) {
      console.error('Unable to join account', e)

      const error = e.data || {}
      switch (error.type) {
        case 'VALIDATION_ERR':
          processError(error.errors[0])
          break

        default:
          processError(error)
      }
    }
  }

  return (
    <EntryFormContainer>
      {errors.apiError && (
        <EntryFormError>{errors.apiError.message || 'Unable to accept invitation. Try again'}</EntryFormError>
      )}

      <form className={styles.Form} onSubmit={handleSubmit(onFormSubmit)}>
        <FormControl>
          <Label>Name</Label>
          <Input {...register('name')} />
          {errors.name && <FormErrorText>{errors.name.message}</FormErrorText>}
        </FormControl>

        <FormControl>
          <Label>Password</Label>
          <Input type='password' {...register('password')} />
          {errors.password && <FormErrorText>{errors.password.message}</FormErrorText>}
        </FormControl>

        <FormControl>
          <Label>Confirm Password</Label>
          <Input type='password' {...register('rePassword')} />
          {errors.rePassword && <FormErrorText>{errors.rePassword.message}</FormErrorText>}
        </FormControl>

        <Button type='submit' variant='primary' disabled={acceptInviteResult.isLoading}>
          {acceptInviteResult.isLoading ? 'Joining Account' : 'Join Account'}
        </Button>
      </form>
    </EntryFormContainer>
  )
}

const InProgress = () => {
  return (
    <EntryFormContainer className={styles.ProgressContainer}>
      <Spinner className={styles.StatusIcon} />
      <p>
        Please wait... <br />
        We are verifying your account
      </p>
    </EntryFormContainer>
  )
}

const Error = () => {
  return (
    <EntryFormContainer className={styles.ProgressContainer}>
      <Heading className={styles.StatusIcon}>😕</Heading>
      <p>
        Invitation token expired or invalid <br />
        Try again or contact <Link href='mailto:support@happyfox.com'>support@happyfox.com</Link>
      </p>
    </EntryFormContainer>
  )
}

const Join = () => {
  const { token } = useParams()
  const { isLoading, isError, isSuccess, data: user } = useVerifyTokenQuery(token)

  return (
    <EntryForm>
      {isLoading && <InProgress />}
      {isError && <Error />}
      {isSuccess && <JoinForm user={user} token={token} />}
    </EntryForm>
  )
}

export default Join
